import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useApi } from '../api/context';
import type { Tenant, RunSummary, Finding, Primitive, MsPillar } from '../api/client';

/**
 * The customer being looked at, held once and shared by every page.
 *
 * Without this the pillar pages have no idea which customer is on screen, so
 * they can only show the catalogue: the same list for everybody. That was the
 * original mistake. A consultant picks a customer once and every pillar,
 * summary and finding follows.
 *
 * The selection survives a reload through session storage rather than local,
 * for the same reason the auth token does: this is a consultant's laptop and
 * the session should end with the browser.
 */

const STORAGE_KEY = 'swo.selectedTenantId';

export interface CustomerState {
  tenants: Tenant[];
  tenant: Tenant | null;
  selectTenant: (tenantId: string) => void;

  runs: RunSummary[];
  run: RunSummary | null;
  selectRun: (runId: string) => void;

  findings: Finding[];
  primitives: Primitive[];

  /** primitiveId -> Microsoft Zero Trust pillar, for grouping findings. */
  pillarOf: (primitiveId: string | null) => MsPillar | null;
  findingsForPillar: (pillar: MsPillar) => Finding[];

  loading: boolean;
  error: string | null;
  reload: () => void;
}

const CustomerContext = createContext<CustomerState | null>(null);

export function CustomerProvider({ children }: { children: ReactNode }) {
  const api = useApi();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(
    () => sessionStorage.getItem(STORAGE_KEY),
  );
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [runId, setRunId] = useState<string | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [primitives, setPrimitives] = useState<Primitive[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // Tenants and the primitive catalogue, once.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.getTenants(), api.getPrimitives()])
      .then(([t, p]) => {
        if (cancelled) return;
        setTenants(t);
        setPrimitives(p);
        setError(null);
        // Fall back to the first customer rather than showing an empty shell.
        if (!tenantId && t.length > 0 && t[0]) setTenantId(t[0].tenantId);
      })
      .catch((e: Error) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
     
  }, [api, nonce]);

  // Runs for the selected customer.
  useEffect(() => {
    if (!tenantId) { setRuns([]); setRunId(null); return; }
    let cancelled = false;
    sessionStorage.setItem(STORAGE_KEY, tenantId);

    api.getRuns(tenantId)
      .then((r) => {
        if (cancelled) return;
        setRuns(r);
        // The most recent run that actually finished. A failed run holds a
        // partial picture, and defaulting to it would present that partial
        // picture as the customer's posture.
        const usable = r.find((x) => x.status === 'complete') ?? r[0] ?? null;
        setRunId(usable ? usable.runId : null);
      })
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [api, tenantId, nonce]);

  // Findings for the selected run.
  useEffect(() => {
    if (!runId) { setFindings([]); return; }
    let cancelled = false;
    api.getFindings(runId)
      .then((f) => !cancelled && setFindings(f))
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [api, runId, nonce]);

  const pillarByPrimitive = useMemo(() => {
    const map = new Map<string, MsPillar>();
    for (const p of primitives) map.set(p.primitiveId, p.msPillar);
    return map;
  }, [primitives]);

  const pillarOf = useCallback(
    (primitiveId: string | null) =>
      primitiveId ? pillarByPrimitive.get(primitiveId) ?? null : null,
    [pillarByPrimitive],
  );

  const findingsForPillar = useCallback(
    (pillar: MsPillar) => findings.filter((f) => pillarOf(f.primitiveId) === pillar),
    [findings, pillarOf],
  );

  const value: CustomerState = {
    tenants,
    tenant: tenants.find((t) => t.tenantId === tenantId) ?? null,
    selectTenant: setTenantId,
    runs,
    run: runs.find((r) => r.runId === runId) ?? null,
    selectRun: setRunId,
    findings,
    primitives,
    pillarOf,
    findingsForPillar,
    loading,
    error,
    reload,
  };

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
}

export function useCustomer(): CustomerState {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error('useCustomer must be used inside CustomerProvider');
  return ctx;
}
